import {
  computed,
  inject,
  onBeforeUnmount,
  provide,
  reactive,
  ref,
  shallowReactive,
  type ComputedRef,
  type InjectionKey,
  type Ref,
} from "vue";

export enum ECompactFilter {
  ALL = "all",
  ANSWERED = "answered",
  UNANSWERED = "unanswered",
  HIDDEN = "hidden",
}

type TSectionState = {
  hasFields: boolean;
  isAnswered: boolean;
};

export type TCompactScope = {
  isCompact: Ref<boolean>;
  isReadonly: Ref<boolean>;
  isCustomizing: Ref<boolean>;
  defaultFilter: ComputedRef<ECompactFilter>;
  filter: Ref<ECompactFilter>;
  total: ComputedRef<number>;
  answered: ComputedRef<number>;
  sectionCount: ComputedRef<number>;
  registerSection: (id: symbol, state: ComputedRef<TSectionState>) => void;
  unregisterSection: (id: symbol) => void;
  hiddenCount: ComputedRef<number>;
  registerHideable: (id: symbol, isHidden: Ref<boolean>) => void;
  unregisterHideable: (id: symbol) => void;
};

type TSectionFields = {
  title: Ref<string | undefined>;
  registerField: (id: symbol, isAnswered: Ref<boolean>, hasError: Ref<boolean>) => void;
  unregisterField: (id: symbol) => void;
};

export const COMPACT_SCOPE_KEY: InjectionKey<TCompactScope> = Symbol("compactScope");

export const SECTION_FIELDS_KEY: InjectionKey<TSectionFields | null> = Symbol("sectionFields");

type TCompactScopeOptions = {
  isCompact: Ref<boolean>;
  isReadonly?: Ref<boolean>;
  isCustomizing?: Ref<boolean>;
};

export const createCompactScope = ({
  isCompact,
  isReadonly = ref(false),
  isCustomizing = ref(false),
}: TCompactScopeOptions): TCompactScope => {
  const sections = reactive(new Map<symbol, ComputedRef<TSectionState>>());
  const defaultFilter = computed(() =>
    isReadonly.value ? ECompactFilter.ANSWERED : ECompactFilter.ALL
  );
  const filter = ref(defaultFilter.value);

  const withFields = computed(() => [...sections.values()].filter((s) => s.value.hasFields));

  const hideables = shallowReactive(new Map<symbol, Ref<boolean>>());

  return {
    isCompact,
    isReadonly,
    isCustomizing,
    defaultFilter,
    filter,
    total: computed(() => withFields.value.length),
    sectionCount: computed(() => sections.size),
    answered: computed(() => withFields.value.filter((s) => s.value.isAnswered).length),
    registerSection: (id, state) => {
      sections.set(id, state);
    },
    unregisterSection: (id) => {
      sections.delete(id);
    },
    hiddenCount: computed(() => [...hideables.values()].filter((hidden) => hidden.value).length),
    registerHideable: (id, isHidden) => {
      hideables.set(id, isHidden);
    },
    unregisterHideable: (id) => {
      hideables.delete(id);
    },
  };
};

const outOfScope: TCompactScope = {
  isCompact: computed(() => false),
  isReadonly: computed(() => false),
  isCustomizing: computed(() => false),
  defaultFilter: computed(() => ECompactFilter.ALL),
  filter: ref(ECompactFilter.ALL),
  total: computed(() => 0),
  sectionCount: computed(() => 0),
  answered: computed(() => 0),
  registerSection: () => undefined,
  unregisterSection: () => undefined,
  hiddenCount: computed(() => 0),
  registerHideable: () => undefined,
  unregisterHideable: () => undefined,
};

export const useCompactScope = () => inject(COMPACT_SCOPE_KEY, outOfScope);

export const useIsInCompactScope = () => inject(COMPACT_SCOPE_KEY, outOfScope) !== outOfScope;

export const useCompactScopeBoundary = () => {
  provide(COMPACT_SCOPE_KEY, outOfScope);
  provide(SECTION_FIELDS_KEY, null);
};

const someTrue = (refs: Iterable<Ref<boolean>>) => {
  for (const item of refs) if (item.value) return true;
  return false;
};

export const createSectionFields = (title: Ref<string | undefined>) => {
  const fields = reactive(new Map<symbol, Ref<boolean>>());
  const errors = reactive(new Map<symbol, Ref<boolean>>());

  return {
    hasFields: computed(() => fields.size > 0),
    isAnswered: computed(() => someTrue(fields.values())),
    hasError: computed(() => someTrue(errors.values())),
    registry: {
      title,
      registerField: (id: symbol, isAnswered: Ref<boolean>, hasError: Ref<boolean>) => {
        fields.set(id, isAnswered);
        errors.set(id, hasError);
      },
      unregisterField: (id: symbol) => {
        fields.delete(id);
        errors.delete(id);
      },
    } satisfies TSectionFields,
  };
};

export const isAnsweredValue = (value: unknown) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const useSectionTitle = () => inject(SECTION_FIELDS_KEY, null)?.title;

export const useSectionFieldRegistration = (isAnswered: Ref<boolean>, hasError: Ref<boolean>) => {
  const section = inject(SECTION_FIELDS_KEY, null);
  const id = Symbol("sectionField");

  section?.registerField(id, isAnswered, hasError);

  onBeforeUnmount(() => {
    section?.unregisterField(id);
  });
};
